import { BaseComponent } from "../../../../base/base.component";
import { Component, OnInit } from "@angular/core";
import { LIST_SELECTOR } from "../../../../../crossconcern/utilities/properties/selector.property";
import { ListInfo } from "../../../../../datastore/entities/listinfo.model";
import { ButtonsModel } from "../../../../../crossconcern/models/tablelist/button.model";
import { TableInfo } from "../../../../../crossconcern/models/tablelist/tableinfo.model";
import { NurseryModel } from "../../../../../datastore/entities/nursery.model";
import { AppComponent } from "../../../../app.component";
import { Router } from "@angular/router";
import { NurseryManager } from "../../../../../business/managers/nursery.manager";
import { TableInfoHeader } from "../../../../../crossconcern/models/tablelist/tableinfoheader.model";
import { Location } from "@angular/common";
import { TABLELIST_ITEMS_PER_PAGE } from "../../../../../crossconcern/utilities/properties/base.property";
import {
    TABLELIST_BUTTON_COLOR_4, TABLELIST_BUTTON_COLOR_5, TABLELIST_BUTTON_COLOR_8,
    TABLELIST_BUTTON_ICON_CLASS_CLONE, TABLELIST_BUTTON_ICON_CLASS_DELETE, TABLELIST_BUTTON_ICON_CLASS_VIEW
} from "../../../../../crossconcern/utilities/properties/tablelist.property";
import { finalize } from "rxjs/operators";
import { AlertUtils } from "../../../../../crossconcern/utilities/utils/alert.utils";
import {
    PRIVATE_NURSERY_ADD_ROUTE,
    PRIVATE_NURSERY_DETAIL_ROUTE,
    PRIVATE_NURSERY_EDIT_ROUTE
} from "../../../../../crossconcern/utilities/properties/routename.property";
import { PhotosphereManager } from "../../../../../business/managers/photosphere.manager";
import { Subscription } from "rxjs";
import { GenericService } from "app/crossconcern/services/generic.services";
import { logData } from "../../../../../crossconcern/helpers/generic/generic.helper";

@Component({
    selector: LIST_SELECTOR,
    templateUrl: "./nurserylist.component.html"
})
export class NurseryListComponent extends BaseComponent implements OnInit {
    public nurseries: NurseryModel[];
    public listInfo: ListInfo;
    public tableInfo: TableInfo;
    public searchText: string;

    private selectedFilters: Map<string, unknown>;
    private sortField: string;
    private sortAsc: boolean;
    private currentPage = 0;

    private onNewCalledSubscription: Subscription;

    constructor(
        protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        private nurseryManager: NurseryManager,
        private photosphereManager: PhotosphereManager,
        protected genericService: GenericService,
        protected alertUtils: AlertUtils) {
        super(appComponent, router, alertUtils, location);
        this.selectedFilters = new Map<string, unknown>();
        this.subscribeGenericServices();
    }

    public ngOnInit() {
        this.getNurseries();
    }

    public onViewDetailClick(index: number) {
        const nursery = this.nurseries[index];
        this.goToRouteByName(PRIVATE_NURSERY_DETAIL_ROUTE, { id: nursery.uuid });
    }

    public onEditClick(index: number) {
        const nursery = this.nurseries[index];
        this.goToRouteByName(PRIVATE_NURSERY_EDIT_ROUTE, { id: nursery.uuid });
    }

    public onDeleteClick(index: number) {
        const nursery = this.nurseries[index];
        this.alertUtils.showConfirmAlert(
            "NURSERY_LIST_COMPONENT_DELETE_CONFIRM_TITLE_ALERT",
            "NURSERY_LIST_COMPONENT_DELETE_CONFIRM_TEXT_ALERT",
            () => {
                this.appComponent.startLoading();
                this.nurseryManager.deleteNursery(nursery.uuid)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    )
                    .subscribe(() => {
                        this.getNurseries();
                    });
            },
            () => {
                logData("close");
            }
        );
    }

    public onCloneClick(index: number) {
        const nursery = this.nurseries[index];
        this.alertUtils.showConfirmAlert(
            "NURSERY_LIST_COMPONENT_CLONE_CONFIRM_TITLE_ALERT",
            "NURSERY_LIST_COMPONENT_CLONE_CONFIRM_TEXT_ALERT",
            () => {
                this.appComponent.startLoading();
                this.nurseryManager.duplicateNursery(nursery.uuid)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    )
                    .subscribe(() => {
                        this.getNurseries();
                    });
            },
            () => {
                logData("close");
            }
        );
    }

    public getNurseries() {
        this.appComponent.startLoading();
        this.nurseryManager.getNurseries(null, 15, this.currentPage).pipe(
            finalize(() => this.appComponent.stopLoading())
        ).subscribe(
            ([
                listInfo, nurseries
            ]) => {
                this.listInfo = listInfo;
                this.nurseries = nurseries;
                this.setTable();
            });
    }

    public onSyncProducts() {
        this.appComponent.startLoading();
        this.photosphereManager.syncProducts()
            .pipe(
                finalize(() => this.appComponent.stopLoading())
            )
            .subscribe();
    }

    public onSearchConfirmed(searchText: string) {
        this.searchText = searchText;
        this.currentPage = 0;
        this.getNurseries();
    }

    public onPageChanged(num: number) {
        this.currentPage = num;
        this.getNurseries();
    }

    public onHeaderClick(sort: [string, boolean]) {
        this.sortField = sort[0];
        this.sortAsc = sort[1];
        this.getNurseries();
    }

    private setTable() {
        const headers = {
            "name": new TableInfoHeader(
                "NURSERY_LIST_COMPONENT_HEADER_NAME",
                false,
                this.sortAsc === true,
                this.sortField === "name"
            ),
            "address": new TableInfoHeader(
                "NURSERY_LIST_COMPONENT_HEADER_ADDRESS",
                false,
                this.sortAsc === true,
                this.sortField === "address"
            ),
            "actions": new TableInfoHeader(
                "NURSERY_LIST_COMPONENT_HEADER_ACTIONS",
                false,
                false,
                false
            )
        };

        const items: unknown[] = [];
        for (const nursery of this.nurseries) {
            items.push({
                "name": (nursery.name) ? nursery.name : "",
                "address": (nursery.address) ? nursery.address : ""
            });
        }

        const buttons: ButtonsModel[] = [];
        buttons.push(new ButtonsModel(
            "NURSERY_COMPONENT_TABLE_BUTTON_VIEW",
            TABLELIST_BUTTON_COLOR_4,
            TABLELIST_BUTTON_ICON_CLASS_VIEW,
            (item) => {
                this.onViewDetailClick(item as number);
            })
        );
        buttons.push(new ButtonsModel(
            "NURSERY_LIST_COMPONENT_TABLE_BUTTON_CLONE",
            TABLELIST_BUTTON_COLOR_5,
            TABLELIST_BUTTON_ICON_CLASS_CLONE,
            (item) => {
                this.onCloneClick(item as number);
            })
        );
        buttons.push(new ButtonsModel(
            "PROMOTION_LIST_COMPONENT_TABLE_BUTTON_DELETE",
            TABLELIST_BUTTON_COLOR_8,
            TABLELIST_BUTTON_ICON_CLASS_DELETE,
            (item) => {
                this.onDeleteClick(item as number);
            })
        );

        this.tableInfo = new TableInfo(headers, items, TABLELIST_ITEMS_PER_PAGE, buttons);
    }

    private subscribeGenericServices() {
        this.onNewCalledSubscription = this.genericService.onNewCalled.subscribe(() => {
            this.goToRouteByName(PRIVATE_NURSERY_ADD_ROUTE);
        });
    }

}
